import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/Field';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useReportAbsence } from '../../hooks/useReportAbsence';
import type { ScheduleOccurrence } from '../../hooks/useSchedule';
import { isEligibleForMakeupCredit } from '@court-sync/shared';

interface ReportAbsenceFromCalendarProps {
  occurrence: ScheduleOccurrence;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ReportAbsenceFromCalendar({
  occurrence,
  isOpen,
  onClose,
  onSuccess,
}: ReportAbsenceFromCalendarProps) {
  const [reason, setReason] = useState('');
  const { reportAbsence, isLoading } = useReportAbsence();

  const lessonStart = new Date(occurrence.date);
  const [hours, minutes] = occurrence.startTime.split(':').map(Number);
  lessonStart.setHours(hours, minutes, 0, 0);

  const now = new Date();
  const eligible = isEligibleForMakeupCredit(
    now,
    lessonStart,
    occurrence.makeupNotificationDeadlineHours
  );

  const handleSubmit = async () => {
    try {
      await reportAbsence(occurrence.bookingId, reason, {
        lessonDate: format(occurrence.date, 'yyyy-MM-dd'),
        studentId: occurrence.studentId,
        ...(occurrence.sessionId && { sessionId: occurrence.sessionId }),
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report Absence</DialogTitle>
          <DialogDescription>
            Report that {occurrence.lessonName} on{' '}
            {format(occurrence.date, 'EEEE, MMMM d')} at {occurrence.startTime}{' '}
            will be missed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {occurrence.makeupLessonEnabled && (
            <>
              {eligible ? (
                <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    You're within the deadline. Reporting now will earn you a
                    make-up credit valid for {occurrence.lessonType} lessons
                    {occurrence.skillLevel && ` (${occurrence.skillLevel})`}.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    The deadline has passed. Absence must be reported at least{' '}
                    {occurrence.makeupNotificationDeadlineHours} hours before the
                    lesson to earn a credit.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          <Field>
            <FieldLabel htmlFor="absence-reason">Reason (optional)</FieldLabel>
            <Textarea
              id="absence-reason"
              placeholder="e.g. Illness, family commitment..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <FieldDescription>
              Providing a reason helps the academy with planning.
            </FieldDescription>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !eligible}
          >
            {isLoading ? 'Reporting...' : 'Report Absence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
